import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { NgxSpinnerService } from 'ngx-spinner';
import { BillingProcess, BillingProcessCreate, BillingProcessPatch } from 'src/app/models/billing-process';
import { Marina } from 'src/app/models/marina';
import { MarinaCompany } from 'src/app/models/marina-company';
import { BillingProcessService } from 'src/app/services/billing-process.service';
import { OperationalConfigService } from 'src/app/services/config/operational-config.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import { MessageUtil } from 'src/app/utils/message.util';

interface SelectOption<T> {
  label: string;
  value: T;
}

const MAIN_COMPANY_KEY = 'MAIN';

@Component({
  selector: 'app-billing-process-form',
  templateUrl: './billing-process-form.component.html',
  styleUrls: ['./billing-process-form.component.scss'],
})
export class BillingProcessFormComponent implements OnInit {
  mode: 'create' | 'edit' = 'create';
  editingId: number | null = null;
  loadingProcess = false;

  marina: Marina;
  marinaCompanies: MarinaCompany[] = [];
  isMultiCompany = false;
  usesFranchise = false;

  marinaCompanyOptions: SelectOption<string>[] = [];
  monthOptions: SelectOption<number>[] = [];
  yearOptions: SelectOption<number>[] = [];

  selectedCompanyKey: string = '';
  description = '';
  referenceMonth: number;
  referenceYear: number;
  franchiseReferenceMonth: number | null = null;
  franchiseReferenceYear: number | null = null;

  saving = false;
  private descriptionTouched = false;

  private readonly monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  constructor(
    private billingProcessService: BillingProcessService,
    private operationalConfigService: OperationalConfigService,
    private router: Router,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private messageService: MessageUtil,
    private confirmationService: ConfirmationService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.marina = StorageUtil.getMarina();
    this.marinaCompanies = this.marina?.marinaCompanies ?? [];
    this.isMultiCompany = this.marinaCompanies.length > 0;

    this.buildMonthOptions();
    this.buildYearOptions();
    this.buildCompanyOptions();

    try {
      this.usesFranchise = await this.operationalConfigService.isYatchClub();
    } catch {
      this.usesFranchise = false;
    }

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.mode = 'edit';
      this.editingId = Number(idParam);
      this.loadExistingProcess(this.editingId);
    } else {
      this.mode = 'create';
      const today = new Date();
      this.referenceMonth = today.getMonth() + 1;
      this.referenceYear = today.getFullYear();
      this.refreshDescriptionDefault();
      if (this.usesFranchise) {
        this.applyFranchiseDefault();
      }
      if (!this.isMultiCompany) {
        this.selectedCompanyKey = '';
      }
    }
  }

  private loadExistingProcess(id: number): void {
    this.loadingProcess = true;
    this.spinner.show();
    this.billingProcessService.findById(id).subscribe({
      next: (process) => {
        this.spinner.hide();
        this.loadingProcess = false;
        if (process.status !== 'DRAFT') {
          this.messageService.generateMessage(
            'warn',
            'SUMMARY.WARNING',
            'Apenas processos em rascunho podem ser editados.',
          );
          this.voltar();
          return;
        }
        this.description = process.description;
        this.descriptionTouched = true;
        this.referenceMonth = process.referenceMonth;
        this.referenceYear = process.referenceYear;
        this.franchiseReferenceMonth = process.franchiseReferenceMonth ?? null;
        this.franchiseReferenceYear = process.franchiseReferenceYear ?? null;
        if (this.isMultiCompany) {
          this.selectedCompanyKey = process.marinaCompanyId == null
            ? MAIN_COMPANY_KEY
            : String(process.marinaCompanyId);
        }
      },
      error: (err) => {
        this.spinner.hide();
        this.loadingProcess = false;
        this.messageService.generateMessage(
          'error',
          'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao carregar processo de faturamento.',
        );
        this.voltar();
      },
    });
  }

  private buildMonthOptions(): void {
    this.monthOptions = this.monthNames.map((name, idx) => ({
      label: name,
      value: idx + 1,
    }));
  }

  private buildYearOptions(): void {
    const current = new Date().getFullYear();
    const years: SelectOption<number>[] = [];
    for (let y = current - 2; y <= current + 2; y++) {
      years.push({ label: String(y), value: y });
    }
    this.yearOptions = years;
  }

  private buildCompanyOptions(): void {
    // Marina principal (entidade Marina) entra como primeira opção com value=null.
    // As marina_companies são empresas ADICIONAIS — somam à principal.
    this.marinaCompanyOptions = [
      {
        label: this.marina?.companyName ?? this.marina?.tradeName ?? 'Empresa principal',
        value: MAIN_COMPANY_KEY,
      },
      ...this.marinaCompanies.map((mc) => ({
        label: mc.companyFederalName ?? `Empresa #${mc.id}`,
        value: String(mc.id),
      })),
    ];
  }

  onDescriptionInput(): void {
    this.descriptionTouched = true;
  }

  onPeriodChange(): void {
    this.refreshDescriptionDefault();
    if (this.usesFranchise) {
      this.applyFranchiseDefault();
    }
  }

  private refreshDescriptionDefault(): void {
    if (this.descriptionTouched) {
      return;
    }
    if (this.referenceMonth && this.referenceYear) {
      const monthLabel = this.monthOptions.find((o) => o.value === this.referenceMonth)?.label;
      this.description = monthLabel
        ? `Faturamento ${monthLabel}/${this.referenceYear}`
        : `Faturamento ${this.referenceMonth}/${this.referenceYear}`;
    }
  }

  private applyFranchiseDefault(): void {
    if (!this.referenceMonth || !this.referenceYear) {
      return;
    }
    let month = this.referenceMonth - 1;
    let year = this.referenceYear;
    if (month <= 0) {
      month = 12;
      year -= 1;
    }
    this.franchiseReferenceMonth = month;
    this.franchiseReferenceYear = year;
  }

  isValid(): boolean {
    if (!this.description?.trim()) return false;
    if (!this.referenceMonth || !this.referenceYear) return false;
    if (this.isMultiCompany && !this.selectedCompanyKey) return false;
    if (this.usesFranchise) {
      const hasMonth = this.franchiseReferenceMonth != null;
      const hasYear = this.franchiseReferenceYear != null;
      if (hasMonth !== hasYear) return false;
    }
    return true;
  }

  get headerTitle(): string {
    return this.mode === 'edit'
      ? 'Alteração de Processo de Faturamento'
      : 'Cadastro de Processo de Faturamento';
  }

  get headerIcon(): string {
    return this.mode === 'edit' ? 'pi pi-pencil' : 'pi pi-plus-circle';
  }

  private resolveSelectedCompanyId(): number | null {
    if (!this.isMultiCompany) return null;
    if (!this.selectedCompanyKey || this.selectedCompanyKey === MAIN_COMPANY_KEY) return null;
    const parsed = Number(this.selectedCompanyKey);
    return Number.isFinite(parsed) ? parsed : null;
  }

  voltar(): void {
    this.router.navigate(['/app/billing/billing-process']);
  }

  salvar(): void {
    if (!this.isValid() || this.saving) {
      return;
    }
    this.saving = true;
    this.spinner.show();
    if (this.mode === 'edit' && this.editingId) {
      this.salvarEdicao();
    } else {
      this.salvarCriacao();
    }
  }

  private salvarCriacao(): void {
    const payload: BillingProcessCreate = {
      marinaCompanyId: this.resolveSelectedCompanyId(),
      description: this.description.trim(),
      referenceMonth: this.referenceMonth,
      referenceYear: this.referenceYear,
      franchiseReferenceMonth: this.usesFranchise ? this.franchiseReferenceMonth : null,
      franchiseReferenceYear: this.usesFranchise ? this.franchiseReferenceYear : null,
    };
    this.billingProcessService.create(payload).subscribe({
      next: (process: BillingProcess) => {
        this.spinner.hide();
        this.saving = false;
        if (process.existing) {
          this.confirmationService.confirm({
            header: 'Já existe um processo em andamento',
            message: `Já existe o processo "${process.description}" (status ${process.status}) para esse período/empresa. Deseja abri-lo?`,
            icon: 'pi pi-info-circle',
            acceptLabel: 'Sim, abrir',
            rejectLabel: 'Voltar',
            accept: () => this.router.navigate(['/app/billing/billing-process', process.id, 'edit']),
            reject: () => this.voltar(),
          });
          return;
        }
        this.messageService.generateMessage('success', 'SUMMARY.SUCCESS', 'Processo criado.');
        this.voltar();
      },
      error: (err) => {
        this.spinner.hide();
        this.saving = false;
        this.messageService.generateMessage(
          'error',
          'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao criar processo de faturamento.',
        );
      },
    });
  }

  private salvarEdicao(): void {
    const payload: BillingProcessPatch = {
      description: this.description.trim(),
      franchiseReferenceMonth: this.usesFranchise ? this.franchiseReferenceMonth : null,
      franchiseReferenceYear: this.usesFranchise ? this.franchiseReferenceYear : null,
    };
    this.billingProcessService.patch(this.editingId, payload).subscribe({
      next: () => {
        this.spinner.hide();
        this.saving = false;
        this.messageService.generateMessage('success', 'SUMMARY.SUCCESS', 'Processo atualizado.');
        this.voltar();
      },
      error: (err) => {
        this.spinner.hide();
        this.saving = false;
        this.messageService.generateMessage(
          'error',
          'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao atualizar processo de faturamento.',
        );
      },
    });
  }
}
